import { Mesh } from 'three';
import type { WorldFeature } from '../types';
import type { Vec3 } from '../../geo/types';
import { hexToLinear } from '../../core/color';
import { createMeshBuilder, transformPoint, type VertexStyle } from '../mesh/meshBuilder';
import { withClothWind, type ClothSpan } from '../mesh/clothWind';
import { SURFACE, edgeIdFor } from '../mesh/attributes';
import { planPoint, beamSolid } from '../fabric/solids';
import { stadhuisPlanOf } from '../fabric/landmarks/stadhuis/stadhuisPlan';
import { meshFrameOf } from '../fabric/landmarks/planFrame';
import { painterPlaced } from '../fabric/landmarks/painterBaseLine';
import { createGroundElevation } from '../../site/groundElevation';
import { foundationHeight } from '../fabric/foundation';

/** Heraldic hanging cloth; placement, cut and mounts are reconstruction choices. */
export const stadhuisBannersFeature: WorldFeature = {
  id: 'stadhuis-banners',
  label: 'Town hall banners',
  weight: 0.2,
  build(ctx) {
    const hall = ctx.layout.landmarks.find((l) => l.id === 'landmark-oude-stadhuis');
    if (!hall) return { objects: [], obstacles: [], animated: [], counts: {} };
    const plan = stadhuisPlanOf(hall),
      base = foundationHeight(hall.footprint, createGroundElevation(ctx.layout));
    const frame = {
      ...meshFrameOf(plan.frame.plan),
      origin: [plan.frame.plan.origin[0], base, -plan.frame.plan.origin[1]] as Vec3,
    };
    const raw = createMeshBuilder(),
      builder = painterPlaced(raw, hall.id),
      spans: ClothSpan[] = [];
    const style = (colour: string): VertexStyle => ({
      color: hexToLinear(colour),
      surface: SURFACE.cloth,
      edgeId: edgeIdFor(1841),
      extent: [1, 3.7],
      strokeAngle: 0,
    });
    const red = style('#8d3329'),
      black = style('#292722'),
      white = style('#d5cbb0');
    const positions = [
      { u: plan.vierschaar.u1 / 3, v: -0.45, top: 11.9 },
      { u: (plan.vierschaar.u1 * 2) / 3, v: -0.45, top: 11.9 },
      { u: plan.hall.u0 + 0.65, v: plan.hall.v0 - 0.45, top: 10.5 },
      { u: plan.hall.u1 - 0.65, v: plan.hall.v0 - 0.45, top: 10.5 },
    ];
    positions.forEach(({ u, v, top }, k) => {
      const width = 1.0,
        height = 3.7,
        start = builder.vertexCount;
      const at = (x: number, drop: number, proud = 0): Vec3 =>
        planPoint(u + x, top - drop, v - proud);
      for (const [x0, x1, colour] of [
        [-width / 2, -0.22, red],
        [-0.22, 0.22, black],
        [0.22, width / 2, red],
      ] as const)
        for (let row = 0; row < 24; row++) {
          const a = (row * height) / 24,
            b = ((row + 1) * height) / 24;
          builder.quad(colour, frame, [at(x0, a), at(x0, b), at(x1, b), at(x1, a)]);
        }
      for (const centre of [0.85, 1.85, 2.85])
        for (const slope of [-1, 1])
          for (const face of [-1, 1])
            for (let part = 0; part < 6; part++) {
              const a = -0.19 + (part * 0.38) / 6,
                b = -0.19 + ((part + 1) * 0.38) / 6,
                half = 0.05;
              builder.quad(white, frame, [
                at(a, centre + slope * a - half, face * 0.008),
                at(a, centre + slope * a + half, face * 0.008),
                at(b, centre + slope * b + half, face * 0.008),
                at(b, centre + slope * b - half, face * 0.008),
              ]);
            }
      const heading = frame.yaw;
      spans.push({
        start,
        end: builder.vertexCount,
        anchor: transformPoint(at(0, 0), frame),
        along: [0, -1, 0],
        normal: [Math.sin(heading), 0, Math.cos(heading)],
        length: height,
        amplitude: 0.2,
        phase: k * 1.7,
      });
      beamSolid(
        builder,
        { ...red, color: hexToLinear('#695139'), surface: SURFACE.wood },
        frame,
        at(-0.62, -0.025),
        at(0.62, -0.025),
        0.035
      );
      for (const side of [-1, 1])
        beamSolid(
          builder,
          { ...red, color: hexToLinear('#695139'), surface: SURFACE.wood },
          frame,
          planPoint(u + side * 0.42, top, v + 0.45),
          at(side * 0.42, 0),
          0.025
        );
    });
    const mesh = new Mesh(
      withClothWind(raw.build(), spans),
      ctx.materials.painted({ doubleSided: true, clothWind: true })
    );
    mesh.name = 'stadhuis-heraldic-wind-banners';
    return {
      objects: [mesh],
      obstacles: [],
      animated: [],
      counts: { stadhuisBanners: positions.length },
    };
  },
};
